/**
 * The navigation registry — one entry per `nav_entries` value, in order.
 *
 * `nav_shape` is `navbar` on this surface: the entries sit HORIZONTALLY in the
 * top bar from the tablet tier up, and collapse to the bottom bar below it.
 * There is no side column. The shape is a property of the surface, not of the
 * viewport — a browse-and-watch surface's value is the artwork across the full
 * container width, and a 232px column would take a fifth of every page to
 * repeat four destinations.
 *
 * The category tree hangs off the `categories` entry as a native `<details>`
 * disclosure panel, which is what `nav_shape: navbar` means for the tree.
 *
 * The four entries are deliberately identical to the public site's — the two
 * surfaces differ by SESSION, not by catalogue, so browsing does not change
 * shape because someone signed in.
 *
 * The account destinations are NOT nav entries: they live in the account menu,
 * which exists only where a session does. The phone bar is fixed at four
 * items, and putting three account destinations there costs the subscriber the
 * catalogue and still needs seven slots.
 *
 * ONE link family leaves this host, and it is now the two informational pages
 * alone — من نحن and اتصل بنا, which the public site genuinely owns.
 *
 * **The editorial listing left that family**, and it left for the reason the
 * catalogue did, one step later. `shahedni.com` is a separate cookie scope by
 * design — this platform issues no parent-domain cookie — so المقالات opened a
 * new tab in which the subscriber was not signed in, and the post it reached
 * could only offer them «سجّل الدخول للتعليق» for a session they were already
 * holding. The thread is the whole reason a signed-in reader opens an article,
 * and it is the one thing the public host can never compose. `/blog` and
 * `/blog/:slug` are routes here now; the public compositions stay where they
 * are indexed, and their own wall link points back at these.
 *
 * **The catalogue is not in that family any more.** Search, the genre tree and
 * the tag chips are the reasons a subscriber signed in, and sending them to the
 * public host threw away the open player, the scroll position and the whole
 * client cache to run a query this host can run — and answered it WITHOUT the
 * session, so nothing on the results could be opened in place. The tree was
 * worse than costly: every one of its rows carried an Arabic slug, and the
 * develop edge cannot proxy a non-ASCII path at all, so each row reached a 404.
 */
import { SITE_ORIGIN } from '@shared/api/config';

import { chromeCopy } from './copy';

export type NavEntryKind =
  /** A route on this host. */
  | 'route'
  /** A listing served on the public host. */
  | 'external'
  /** Opens the genre-tree disclosure panel rather than navigating. */
  | 'disclosure';

export type NavEntryId = 'home' | 'categories' | 'search' | 'editorial';

export interface NavEntry {
  id: NavEntryId;
  label: string;
  kind: NavEntryKind;
  /** `null` where the entry has no resolved destination — see `browseHref`. */
  href: string | null;
}

export const navEntries: readonly NavEntry[] = [
  { id: 'home', label: chromeCopy.nav.home, kind: 'route', href: '/' },
  { id: 'categories', label: chromeCopy.nav.categories, kind: 'disclosure', href: null },
  { id: 'search', label: chromeCopy.nav.search, kind: 'route', href: '/search' },
  // A ROUTE, not a cross-host link, since the editorial listing and the post
  // page are served here. See the block comment above for why the family moved.
  { id: 'editorial', label: chromeCopy.nav.editorial, kind: 'route', href: '/blog' },
];

/**
 * The attributes every cross-host chrome link carries.
 *
 * **A NEW TAB, ALWAYS, AND THIS IS THE ONE PLACE IT IS DECIDED.** These links
 * leave a SPA the reader is signed into for a server-rendered site that is not
 * theirs, and replacing the tab threw away everything the app was holding — an
 * open player, a half-written case, a scrolled position, the whole client
 * cache — to read one article. Coming back meant a cold boot of the bundle and
 * a fresh silent refresh. The public site is a REFERENCE from inside the app,
 * so it opens beside it.
 *
 * `rel` is not optional decoration: `target="_blank"` without `noopener` hands
 * the opened document a live `window.opener` handle back into this origin.
 */
export const CROSS_HOST_LINK = {
  target: '_blank',
  rel: 'noopener noreferrer',
} as const;

/**
 * The categories entry's destination, resolved from the live vocabulary.
 *
 * There is no bare `/genres` route on EITHER host — `frontend-landing`'s
 * PRD-TDD §7.1 lists it among the routes that deliberately do not exist, «the
 * tree and the chip strip are the vocabularies' surfaces», and this repo made
 * the same choice for the same reason. What the landing pack DOES name as the
 * standalone browse page is the category landing (§3.12, §4.6), so the
 * destination is the first root term's own page — now `/genres/<slug>` HERE
 * rather than on the public host.
 *
 * That slug is operator-held data, which is why it cannot be spelled in this
 * module and is resolved from `useCategories()` at the render instead.
 *
 * `null` while the vocabulary is empty or still loading. Every caller drops its
 * link in that state rather than substituting one — a control that does not
 * navigate is better than one that navigates nowhere.
 */
export function browseHref(categories: readonly { readonly slug: string }[] | undefined): string | null {
  const firstRoot = categories?.[0]?.slug;
  return firstRoot ? `/genres/${encodeURIComponent(firstRoot)}` : null;
}

/** One genre listing, on this host. The slug is the public site's own segment. */
export function genreHref(slug: string): string {
  return `/genres/${encodeURIComponent(slug)}`;
}

/** One tag listing, on this host. */
export function tagHref(slug: string): string {
  return `/tags/${encodeURIComponent(slug)}`;
}

export type AccountEntryId = 'profile' | 'lists' | 'subscription' | 'support' | 'settings';

export interface AccountEntry {
  id: AccountEntryId;
  label: string;
  href: string;
}

/**
 * The account menu — present only where a session is. Sign-out is deliberately
 * NOT a member: it is an action rather than a destination, and rendering it as
 * a link to `/login` would draw a control that navigates without ending the
 * session it claims to end.
 */
export const accountEntries: readonly AccountEntry[] = [
  { id: 'profile', label: chromeCopy.account.profile, href: '/profile' },
  { id: 'lists', label: chromeCopy.account.lists, href: '/lists' },
  { id: 'subscription', label: chromeCopy.account.subscription, href: '/subscription' },
  { id: 'support', label: chromeCopy.account.support, href: '/cases' },
  { id: 'settings', label: chromeCopy.account.settings, href: '/settings' },
];

export interface FooterEntry {
  id: string;
  label: string;
  href: string;
  /** Served on the public host, so it opens BESIDE the app. Defaults to true. */
  external?: boolean;
}

/**
 * The two informational destinations, which `[x]` §21.4 binds to «every public
 * AND gated surface» by name.
 *
 * They are their own list because the PRE-SESSION shell carries exactly these
 * two and nothing else. The other half of the authenticated footer — search and
 * the editorial listing — are CATALOGUE destinations, and the pre-session
 * screens drop every catalogue destination for one reason: the catalogue is
 * served on the public host and these three screens are a single-task funnel.
 * A sign-in screen that offers four ways out of the task is not the funnel the
 * pack draws, and the reader who wants to browse rather than sign in is one
 * brand click away from the host that serves it.
 */
export const footerInformationalEntries: readonly FooterEntry[] = [
  { id: 'about', label: chromeCopy.footer.about, href: `${SITE_ORIGIN}/about`, external: true },
  { id: 'contact', label: chromeCopy.footer.contact, href: `${SITE_ORIGIN}/contact`, external: true },
];

/**
 * `footer_entries` — about and contact are mandatory members on every platform.
 *
 * `external` is what decides whether an entry opens beside the app or navigates
 * inside it, and the split is not cosmetic: the three public-host entries are
 * documents the public site owns, while search is a surface of THIS app.
 */
export const footerEntries: readonly FooterEntry[] = [
  ...footerInformationalEntries,
  { id: 'search', label: chromeCopy.footer.search, href: '/search', external: false },
  { id: 'editorial', label: chromeCopy.footer.editorial, href: '/blog', external: false },
];
